import { useMemo, useState } from 'react';
import { Shop } from '@/types/shop';
import NaverMap, { NaverMapMarkerItem } from '@/components/common/NaverMap/BaseMap';

interface VendorSelectionMapPanelProps {
  clientId: string;
  selectedShops: Shop[];
  fixedVendorIds: Set<number>;
  selectedMarkerId: number | null;
  onSelectMarker: (shopId: number | null) => void;
  mapMarkerPositionBottom?: number;
  zoomLevel: number;
  onZoomLevelChange: (zoom: number) => void;
  className?: string;
}

function hasCoords(shop: Shop) {
  return typeof (shop as any).lat === 'number' && typeof (shop as any).lng === 'number';
}
function getShortName(name: string) {
  if (!name) return '';
  return name.replace(/\s/g, '').slice(0, 2);
}
export default function VendorSelectionMapPanel({
  clientId,
  selectedShops,
  fixedVendorIds,
  selectedMarkerId,
  onSelectMarker,
  mapMarkerPositionBottom,
  zoomLevel,
  onZoomLevelChange,
  className,
  
}: VendorSelectionMapPanelProps) {
  const [tooltipOpen, setTooltipOpen] = useState<number | null>(null);
  const shopsWithCoords = useMemo(
    () => selectedShops.filter(hasCoords),
    [selectedShops]
  );

  const markers: NaverMapMarkerItem[] = useMemo(
    () =>
      shopsWithCoords.map((shop, index) => ({
        id: shop.id,
        name: shop.shopName,
        lat: Number((shop as any).lat),
        lng: Number((shop as any).lng),
        order: index + 1,
        icon: {
          content: `
            <div style="
              width:32px;
              height:32px;
              border-radius:50%;
              display:flex;
              align-items:center;
              justify-content:center;
              font-size:12px;
              font-weight:700;
              background:white;
              border:2px solid #2563eb;
              color:#2563eb;
              box-shadow:0 2px 6px rgba(0,0,0,0.15);
            ">
              ${getShortName(shop.shopName)}
            </div>
          `,
          // anchor: new window.naver.maps.Point(16, 16),
        },
        html: `
          <div style="padding:10px 12px; min-width:150px;">
            <div style="font-size:13px; font-weight:700; margin-bottom:4px;">
              ${index + 1}. ${shop.shopName}
            </div>
            ${(shop as any).address ? `
              <div style="font-size:12px; color:#6B7280;">
                ${(shop as any).address}
              </div>
            ` : ''}
          </div>
        `,
      })),
    [shopsWithCoords]
  );

  const activeShop =
    tooltipOpen !== null
      ? shopsWithCoords.find((shop) => shop.id === tooltipOpen) ?? null
      : null;
  return (
    <div className={`flex flex-col h-full bg-white ${className ?? ''}`}>
      <div className="relative flex-1 min-h-[420px] bg-gray-50">
        {shopsWithCoords.length === 0 ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="w-14 h-14 flex items-center justify-center rounded-full bg-blue-50 mb-4">
              <i className="ri-map-pin-line text-2xl text-blue-400"></i>
            </div>
            <p className="text-sm font-medium text-gray-500 text-center px-6 leading-relaxed">
              업체를 선택하면
              <br />
              위치를 확인할 수 있습니다
            </p>
          </div>
        ) : (
          <>
            <NaverMap
              clientId={clientId}
              markers={markers}
              selectedMarkerId={selectedMarkerId}
              autoFit={markers.length >= 2}
              zoom={zoomLevel}
              connectLine={false}
              moveToSelectedMarker={false}
              onMarkerClick={(marker) => {
                const shopId = Number(marker.id);
                onSelectMarker(shopId);
                setTooltipOpen((prev) => (prev === shopId ? null : shopId));
              }}
              mapMarkerPositionBottom={mapMarkerPositionBottom}
              onZoomChanged={onZoomLevelChange}
              style={{ width: '100%', height: '100%', minHeight: 420 }}
            />

            <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
              {shopsWithCoords.map((shop, idx) => {
                const isActive = tooltipOpen === shop.id;
                const isFixed = fixedVendorIds.has(shop.id);

                return (
                  <button
                    key={shop.id}
                    onClick={() => {
                      onSelectMarker(shop.id);
                      setTooltipOpen((prev) => (prev === shop.id ? null : shop.id));
                    }}
                    className={`flex items-center gap-2 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all whitespace-nowrap shadow-sm ${
                      isActive
                        ? 'bg-blue-600 text-white scale-105'
                        : isFixed
                        ? 'bg-amber-100 text-amber-800 border border-amber-300'
                        : 'bg-white text-gray-800 border border-gray-200'
                    }`}
                  >
                    <span
                      className={`w-4 h-4 flex items-center justify-center rounded-full text-[10px] font-bold flex-shrink-0 ${
                        isActive
                          ? 'bg-white text-blue-600'
                          : isFixed
                          ? 'bg-amber-400 text-white'
                          : 'bg-blue-600 text-white'
                      }`}
                    >
                      {idx + 1}
                    </span>
                    {shop.shopName}
                  </button>
                );
              })}
            </div>

            {activeShop && (
              <div className="absolute bottom-3 left-3 right-3 bg-white rounded-xl border border-gray-100 px-4 py-3 w-max shadow-sm z-10">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">{activeShop.shopName}</p>
                    {(activeShop as any).address && (
                      <p className="text-xs text-gray-500 mt-0.5 break-words">
                        {(activeShop as any).address}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => setTooltipOpen(null)}
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    <i className="ri-close-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}